import { miroMessageKey } from "utils/miro";
import type { IMiroCanvasImportResult, IMiroCanvasResult, IMiroJobComments, IMiroJobNotes, IMiroJobResult } from "api/miro/miro.type";

export interface CanvasResultSummary {
  /** Main images placed. */
  created: number;
  /** Edit versions placed under their images (only with "Include edit versions"). */
  versions: number;
  /** Main images with nothing Miro can show — neutral, never a failure. */
  skipped: number;
  /** Main images and versions that failed. Labels / text never count here: they are layout, not something the user sent. */
  failed: number;
  /** The distinct failure codes behind `failed`. */
  failedCodes: Array<string | undefined>;
}

/**
 * Reads a canvas export's per-element results the way the user thinks of them: "N exported" counts the `main` results, the
 * `version` ones come back as a separate "+M versions", and `label` results (captions the BE adds for layout) never show up as
 * failures. The job's own progress counts every Miro item created, captions included, so it is never compared with these counts.
 */
export const summarizeCanvasResults = (results: IMiroCanvasResult[]): CanvasResultSummary => {
  // Caption texts are layout, not something the user sent (`label`; a stray `text` from an older BE reads the same).
  const items = results.filter((r) => r.kind !== "label" && (r.kind as string) !== "text");
  const main = items.filter((r) => r.kind !== "version");
  const versions = items.filter((r) => r.kind === "version");
  const failedItems = items.filter((r) => r.status === "failed");

  return {
    created: main.filter((r) => r.status === "created").length,
    versions: versions.filter((r) => r.status === "created").length,
    skipped: main.filter((r) => r.status === "skipped").length,
    failed: failedItems.length,
    failedCodes: Array.from(new Set(failedItems.map((r) => r.error_code))),
  };
};

export interface CanvasImportSummary {
  /** Images on the new canvas — duplicates (already in the user's library) included. */
  imported: number;
  /** Of those, the ones that were new to the library. */
  created: number;
  /** Of those, the ones that were already in the library. */
  duplicate: number;
  /** Images the BE could not import. */
  failed: number;
  /** Non-image items left out (the BE's `skipped` count map, summed). */
  skipped: number;
  /** `"true"` when the BE only counted part of the board's skips (`skipped_partial`): the skipped count is "at least N". ICU `select` value. */
  partial: "true" | "false";
}

/** Reads an `import_canvas` job result: per-image status is created|duplicate|failed, skips only exist in the `skipped` map. */
export const summarizeCanvasImport = (result: IMiroJobResult | null | undefined): CanvasImportSummary => {
  const list = (result?.results ?? []) as IMiroCanvasImportResult[];
  const imported = result?.imported ?? 0;
  // The BE's own counts win; an older BE only has the per-image statuses.
  const duplicate = result?.duplicate ?? list.filter((r) => r.status === "duplicate").length;
  const created = result?.created ?? Math.max(imported - duplicate, 0);

  return {
    imported,
    created,
    duplicate,
    failed: list.filter((r) => r.status === "failed").length,
    skipped: Object.values(result?.skipped ?? {}).reduce((sum, n) => sum + n, 0),
    partial: result?.skipped_partial ? "true" : "false",
  };
};

/** The words for a canvas-import refusal: the 200-image cap and "nothing to import" have their own import copy (the `errors.*` ones are the image import's / the EXPORT's). */
export const canvasImportErrorKey = (code: string | undefined, exists: (key: string) => boolean): string | null => {
  if (code === "miro.too_many_items") return "miro:canvas_import.too_many";
  // A canvas import takes images AND sticky notes (REF-7145): "no images" would not be the whole truth.
  if (code === "miro.nothing_to_import") return "miro:canvas_import.nothing_imported";

  return miroMessageKey(code, "errors", exists);
};

export interface CanvasCommentsSummary {
  /** Export: threads that became (or refreshed) a sticky note. Import: sticky notes that became (or updated) a thread. */
  done: number;
  /** Import only: sticky notes that were already threads on the canvas. */
  duplicate: number;
  /** Threads (export) / sticky notes (import) the BE could not carry over. */
  failed: number;
  /** Export: stickies removed because their thread was deleted from the canvas. */
  removed: number;
  /** Import: stickies left out beyond the per-job cap. */
  skipped: number;
  /** A comments-only problem (rate limit, comments unavailable …): a soft warning, the images are fine. */
  errorCode: string | null;
}

/** One counts block (comments or notes) read the way the user thinks of it; null when the job carried none. */
const summarizeCounts = (c: (IMiroJobComments & IMiroJobNotes) | null | undefined, kind: "export" | "import"): CanvasCommentsSummary | null => {
  if (!c) return null;

  const counts =
    kind === "export"
      ? { done: (c.created ?? 0) + (c.updated ?? 0) + (c.unchanged ?? 0), duplicate: 0, failed: c.failed ?? 0 }
      : { done: (c.imported ?? 0) + (c.updated ?? 0), duplicate: c.duplicate ?? 0, failed: c.failed ?? 0 };
  const extra = { removed: kind === "export" ? c.removed ?? 0 : 0, skipped: kind === "import" ? c.skipped ?? 0 : 0 };
  const summary = { ...counts, ...extra, errorCode: c.error_code ?? null };

  return summary.done + summary.duplicate + summary.failed + summary.removed + summary.skipped > 0 || summary.errorCode ? summary : null;
};

/** The comment part of a canvas job result, or null when the job carried none (so no line is shown at all). */
export const summarizeJobComments = (result: IMiroJobResult | null | undefined, kind: "export" | "import"): CanvasCommentsSummary | null => summarizeCounts(result?.comments, kind);

/**
 * The sticky-note part of a canvas job result (REF-7145, a realtime canvas), or null when the job carried none. Same
 * reading as the comments: export → created + updated + unchanged went across; import → imported + updated (+ duplicate).
 */
export const summarizeJobNotes = (result: IMiroJobResult | null | undefined, kind: "export" | "import"): CanvasCommentsSummary | null => summarizeCounts(result?.notes, kind);
